import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/datetime';

const destinationKind = (destination) => {
  if (!destination) return 'account';
  if (destination.method === 'bank') return 'bank account';
  if (destination.method === 'mpesa') return 'M-Pesa';
  return 'mobile money';
};

const PayoutStatusCard = ({ payout, currency, loading }) => {
  if (loading && !payout) return null;

  const destination = payout?.destination;
  const holdHours = payout?.holdHours || 24;
  const fees = (payout?.flutterwaveFees || 0) + (payout?.platformFees || 0);
  const feePercent = payout?.platformFeePercent || 0;

  const stats = [
    { label: 'Collected', value: payout?.collected || 0 },
    {
      label: 'Fees',
      value: fees,
      hint:
        feePercent > 0
          ? `Processing + Spirara ${feePercent}%`
          : 'Flutterwave processing',
    },
    { label: `Settling (~${holdHours}h)`, value: payout?.settling || 0 },
    { label: 'Sent', value: payout?.sent || 0 },
  ];

  return (
    <div className="bg-sand-50 ring-1 ring-ink-900/10">
      <div className="grid grid-cols-2 gap-px bg-ink-900/10 lg:grid-cols-4">
        {stats.map((item) => (
          <div key={item.label} className="bg-sand-50 px-4 py-4 sm:px-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/60">
              {item.label}
            </p>
            <p className="mt-2 font-display text-2xl font-light tracking-tight text-ink-900">
              {formatCurrency(item.value, currency)}
            </p>
            {item.hint ? (
              <p className="mt-1 text-[11px] leading-snug text-ink-600/55">{item.hint}</p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-4 border-t border-ink-900/10 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/60">
            Pays out to
          </p>
          {payout?.destination?.numberMasked ? (
            <>
              <p className="mt-1 font-medium text-ink-900">
                {destination.name || 'Connected account'}
              </p>
              <p className="mt-0.5 text-sm text-ink-700/80">
                {destination.bank ? `${destination.bank} · ` : ''}
                {destination.numberMasked}
              </p>
              <p className="mt-2 text-sm text-ink-700/80">
                After they clear, cash out to this {destinationKind(destination)} from the top of
                this page.{' '}
                <Link to="/app/manage/payments" className="font-medium text-ink-900 underline">
                  Update payout details
                </Link>
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm text-ink-700/80">
              Add a payout account before online payments can be sent.{' '}
              <Link to="/app/manage/payments" className="font-medium text-ink-900 underline">
                Set payout details
              </Link>
            </p>
          )}
          {(fees > 0 || feePercent > 0) && (
            <p className="mt-2 text-xs text-ink-600/60">
              Fees are Flutterwave processing
              {feePercent > 0 ? ` plus Spirara’s ${feePercent}% platform fee` : ''}
              . Cash out sends what’s left; the platform fee stays in the Spirara wallet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayoutStatusCard;
